import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button, EmptyState, Input, Modal, NoticeBox, Select, Tags } from "../ui";
import { useSubmitGuard } from "../modals/modalHooks";

const locale = (lang) => (lang === "ar" ? "ar-SA" : "en-GB");

export function formatDateTime(iso, lang) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(locale(lang), { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Today: time; this year: day + month; otherwise with the year.
export function formatListStamp(iso, lang) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString(locale(lang), { hour: "2-digit", minute: "2-digit" });
  return date.toLocaleDateString(locale(lang), date.getFullYear() === now.getFullYear() ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "2-digit" });
}

const STATUS_VARIANT = { open: "success", pending: "warning", resolved: "neutral" };

export function StatusTag({ status, t }) {
  return (
    <Tags size="sm" variant={STATUS_VARIANT[status] || "neutral"} title={t.statusHints[status]}>
      {t.statusNames[status] || status}
    </Tags>
  );
}

// Copies `value` to the clipboard and confirms (also for screen readers).
export function CopyButton({ value, label, t }) {
  const [state, setState] = useState("idle");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  if (!value) return null;
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else {
        const field = document.createElement("textarea");
        field.value = value;
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(field);
        if (!ok) throw new Error("copy failed");
      }
      setState("copied");
    } catch {
      setState("failed");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), 1600);
  };
  return (
    <Button variant="neutral" appearance="ghost" size="xs" className="ca-copy" onClick={copy} aria-label={`${t.copy}: ${label}`} title={`${t.copy}: ${label}`} icon={state === "copied" ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}>
      <span className="ca-sr" role="status">{state === "copied" ? t.copied : state === "failed" ? t.copyFailed : ""}</span>
    </Button>
  );
}

export function ResolveDialog({ t, lang, error, onConfirm, onCancel }) {
  const [note, setNote] = useState("");
  const [busy, run] = useSubmitGuard();
  return (
    <Modal
      size="sm"
      dir={lang === "ar" ? "rtl" : "ltr"}
      title={t.resolveTitle}
      description={t.resolveBody}
      onClose={onCancel}
      busy={busy}
      footer={
        <>
          <Button variant="neutral" appearance="outline" disabled={busy} onClick={onCancel}>{t.cancel}</Button>
          <Button variant="primary" appearance="filled" loading={busy} onClick={() => run(() => onConfirm(note.trim()))}>{t.resolve}</Button>
        </>
      }
    >
      <Input textarea rows={3} maxLength={1000} label={t.resolveNote} value={note} onChange={(event) => setNote(event.target.value)} autoFocus />
      {error && <div role="alert"><NoticeBox variant="danger">{error}</NoticeBox></div>}
    </Modal>
  );
}

export function AssignDialog({ t, lang, error, agents, current, onConfirm, onCancel }) {
  const [value, setValue] = useState(current || "");
  const [busy, run] = useSubmitGuard();
  const options = [{ value: "", label: t.unassignOption }, ...(agents.items || []).map((agent) => ({ value: agent.id, label: agent.name || agent.id }))];
  return (
    <Modal
      size="sm"
      dir={lang === "ar" ? "rtl" : "ltr"}
      title={t.assignTitle}
      description={t.assignBody}
      onClose={onCancel}
      busy={busy}
      footer={
        <>
          <Button variant="neutral" appearance="outline" disabled={busy} onClick={onCancel}>{t.cancel}</Button>
          <Button variant="primary" appearance="filled" loading={busy} disabled={agents.status !== "ready" || value === (current || "")} onClick={() => run(() => onConfirm(value || null))}>
            {t.save}
          </Button>
        </>
      }
    >
      {agents.status === "loading" && <EmptyState variant="loading" compact>{t.loadingAgents}</EmptyState>}
      {agents.status === "error" && <EmptyState variant="error" compact>{t.agentsError}</EmptyState>}
      {agents.status === "ready" && <Select label={t.assignee} options={options} value={value} onChange={(event) => setValue(event.target.value)} placeholder={t.unassignOption} />}
      {error && <div role="alert"><NoticeBox variant="danger">{error}</NoticeBox></div>}
    </Modal>
  );
}
